import * as React from "react";
import { Port, PortChildProps, PortSide, PortProps, PortData } from "reaflow";

import { DragEvent, Position } from "reaflow/dist/utils/useNodeDrag";

import { BaseEdgeDataType, BaseNodeDataType } from "../WorkflowUtils";

export type AdditionalPortChildProps = {
  fromNode: BaseNodeDataType;
  isNodeReachable: boolean;
  allNodes: BaseNodeDataType[];
  allEdges: BaseEdgeDataType[];
};

export type BasePortChildPropsType = PortChildProps & AdditionalPortChildProps;

export type BasePortPropsType = Partial<PortProps> & {
  fromNodeId: string;
  allNodes: BaseNodeDataType[];
  allEdges: BaseEdgeDataType[];
  additionalPortChildProps: AdditionalPortChildProps;
  PortChildComponent: React.FC<BasePortChildPropsType>;
  onPortDragEnd(fromNodeId: string, toNodeId?: string): void;
};

export type BasePortDataType = PortData & {};

const BasePort: React.FC<BasePortPropsType> = (props) => {
  const {
    id,
    properties,
    fromNodeId,
    allNodes,
    allEdges,
    additionalPortChildProps,
    PortChildComponent,
    onDragStart: onDragStartInternal,
    onDragEnd: onDragEndInternal,
    onPortDragEnd: onPortDragEndCustom,
  } = props;

  const node: BaseNodeDataType = allNodes.find(
    (node) => node.id === fromNodeId
  ) as BaseNodeDataType;
  const port: BaseNodeDataType = node?.ports?.find(
    (port) => port.id === id
  ) as BaseNodeDataType;

  const style = {
    fill: "white",
    stroke: "#00224D",
  };

  const onPortClick = (
    event: React.MouseEvent<SVGGElement, MouseEvent>,
    port: BaseNodeDataType
  ) => {
    // console.log("onPortClick", port);
  };

  /**
   * Invoked when a port is has started being dragged.
   *
   * Stores the dragged edge in the shared state, to allow other components to know which edge is being dragged.
   *
   * @param event
   * @param fromPosition
   * @param fromPort
   * @param extra
   */
  const onPortDragStart = (
    event: DragEvent,
    fromPosition: Position,
    fromPort: BasePortDataType,
    extra: any
  ) => {
    /* console.log(
      "onDragStart port: ",
      node,
      event,
      fromPosition,
      fromPort,
      extra
    ); */
    if (typeof onDragStartInternal === "function") {
      // Runs internal onDragStart (built-in from Reaflow) which does stuff I'm not aware about
      onDragStartInternal(event, fromPosition, fromPort, extra);
    }
  };

  const onPortDragEnd = (
    dragEvent: DragEvent,
    initial: Position,
    fromPort: BasePortDataType,
    extra: any
  ) => {
    /* console.log(
      "onDragEnd port: ",
      node,
      dragEvent,
      dragEvent.event,
      initial,
      fromPort,
      extra
    );
 */
    const { xy, distance, event } = dragEvent;
    const target = event?.target as Element;

    // Look up in the DOM to find the closest <foreignObject> element that contains the node's id
    const foreignObject: Element | null | undefined =
      target?.closest?.("foreignObject");
    const foreignObjectId: string | undefined = foreignObject?.id;

    const toNodeId: string | undefined = foreignObjectId?.replace(
      "node-foreignObject-",
      ""
    );

    onPortDragEndCustom(fromNodeId, toNodeId);

    if (typeof onDragEndInternal === "function") {
      // Runs internal onDragEnd (built-in from Reaflow) which removes the edge if it doesn't connect to anything
      onDragEndInternal(dragEvent, initial, fromPort, extra);
    }
  };

  const onPortEnter = (
    event: React.MouseEvent<SVGGElement, MouseEvent>,
    port: BasePortDataType
  ) => {
    // console.log("onPortEnter", event.target);
  };

  const onPortLeave = (
    event: React.MouseEvent<SVGGElement, MouseEvent>,
    port: BasePortDataType
  ) => {
    // console.log('onPortLeave', event.target)
  };

  return (
    <Port
      {...props}
      className={`${id} port`}
      onClick={onPortClick}
      onDragStart={onPortDragStart}
      onDragEnd={onPortDragEnd}
      onEnter={onPortEnter}
      onLeave={onPortLeave}
      style={style}
      rx={15}
      ry={15}
    >
      {(portChildProps: PortChildProps) => {
        const basePortChildProps: BasePortChildPropsType = {
          ...portChildProps,
          ...additionalPortChildProps,
        };

        return <PortChildComponent {...basePortChildProps} />;
      }}
    </Port>
  );
};

export default BasePort;
